import axios from 'axios';

// Simple in-memory cache to avoid repeated network calls
let cachedRates = null;
let cachedAt = 0;
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

// Frankfurter migrated from api.frankfurter.app → api.frankfurter.dev/v1 in
// 2025. The old host still answers, but only with a 301 redirect that lacks
// CORS headers — browsers therefore abort the request on the redirect hop,
// which is why our FX widget silently stopped working. We now hit the new
// host directly.
const FX_ENDPOINT = 'https://api.frankfurter.dev/v1/latest';

/**
 * Fetch latest FX rates relative to USD.
 * Returns shape: { base: 'USD', rates: { USD: 1, EUR: number, INR: number }, date: 'YYYY-MM-DD' }
 */
async function getLatestRatesUSD() {
  const now = Date.now();
  if (cachedRates && now - cachedAt < CACHE_TTL_MS) {
    return cachedRates;
  }

  // base=USD makes the response's `rates` relative to USD. We deliberately
  // omit USD from the `symbols` list since the API never echoes the base
  // currency back (its rate is always 1 and we hard-code it below).
  const url = `${FX_ENDPOINT}?base=USD&symbols=EUR,INR`;

  try {
    const { data } = await axios.get(url, { timeout: 8000 });
    const rates = data?.rates || {};

    const normalized = {
      base: 'USD',
      rates: {
        USD: 1,
        EUR: Number(rates?.EUR ?? 0) || 0,
        INR: Number(rates?.INR ?? 0) || 0,
      },
      date: data?.date || null,
    };

    cachedRates = normalized;
    cachedAt = now;
    return normalized;
  } catch (err) {
    // Surface the failure to the console so it's easy to diagnose, but let
    // the caller decide how to handle it (currently: disables FX preview).
    // eslint-disable-next-line no-console
    console.warn('[FXRatesService] Failed to fetch latest rates:', err);
    throw err;
  }
}

const FXRatesService = { getLatestRatesUSD };
export default FXRatesService;
